import projectConfig, { PROJECT } from '~/projects/config';
import { ProductType, REGULARITY } from '~/types/product';

export enum PRODUCT_TAG {
  FREEMIUM_DETECTOR_LIMIT = 'freemium_detectors_limit',
  EXCLUDE_DETECTORS = 'exclude_detectors',
}

export const ANNUAL_PLAN_OLD_PRICE = `600.00 USD`;
export const ANNUAL_PLAN_DISCOUNT = `500.00 USD`;
export const ANNUAL_PLAN_DISCOUNT_PERCENT = 200;

type ProductsConfig = {
  ONETIME_DETECTORS_10_DOLLARS_10K_WORDS_ID: string;
  ONETIME_DETECTORS_20_DOLLARS_20K_WORDS_ID: string;
  ONETIME_DETECTORS_40_DOLLARS_50K_WORDS_ID: string;
  ONETIME_DETECTORS_80_DOLLARS_100K_WORDS_ID: string;
  ANNUAL_SUB_OFFER_ID: string;
  MONTHLY_SUB_OFFER_ID: string;
  UNBLOCK_DETECTOR_TOOLS_10_DOLLARS_ID?: string;
  ANNUAL_SUB_OFFER_80_USD_365_DAYS_ID: string;
  CHAT_PAYWALL_OFFER_IDS: readonly string[];
  SUB_NOT_FOUND_PRODUCTS: ProductType[];
  SUB_NOT_FOUND_PRODUCTS_BY_REGION?: Partial<Record<string, ProductType[]>>;
};

const POLYLLM_ANNUAL_PLAN: ProductType = {
  subscription_type: '',
  trial_currency: '',
  id: 'db0809e6-cf9a-4044-b65f-15960844c378',
  name: 'Annual Plan',
  is_solid: true,
  has_trial_period: false,
  regularity: REGULARITY.YEAR,
  price: 11988,
  currency: 'USD',
  trial_period: 0,
  trial_amount: 0,
  created_at: '2024-01-08T14:50:13Z',
  updated_at: '2024-01-08T14:50:13Z',
  billing_period: 365,
  // @ts-ignore
  description: 'PolyLLM Annual Subscription',
  is_unlimited: true,
  solid_name_for_bi: 'ChatwithAI_plan_120_usd_365days_VAT',
  new_price_ui: 999,
  old_price_ui: 1999,
  discount_percentage_ui: 50,
  regularity_ui: REGULARITY.MONTH,
  description_ui: '',
  details_ui: '',
  is_popular_ui: false,
  // @ts-ignore
  bi_is_upsell: false,
  alternative_non_trial_name: '',
  tags: ['best_price'],
};

const POLYLLM_MONTHLY_PLAN: ProductType = {
  subscription_type: '',
  trial_currency: '',
  id: '2ea46ead-7376-44cd-81e2-c8ebce373fc9',
  name: 'Monthly Plan',
  is_solid: true,
  has_trial_period: false,
  regularity: REGULARITY.MONTH,
  price: 1999,
  currency: 'USD',
  trial_period: 0,
  trial_amount: 0,
  created_at: '2024-01-08T14:50:13Z',
  updated_at: '2024-01-08T14:50:13Z',
  billing_period: 30,
  // @ts-ignore
  description: 'PolyLLM Monthly Subscription',
  is_unlimited: true,
  solid_name_for_bi: 'ChatwithAI_plan_19.99_usd_30days_VAT',
  new_price_ui: 1999,
  old_price_ui: 1999,
  discount_percentage_ui: 0,
  regularity_ui: REGULARITY.MONTH,
  description_ui: '',
  details_ui: '',
  is_popular_ui: true,
  // @ts-ignore
  bi_is_upsell: false,
  alternative_non_trial_name: '',
  tags: [],
};

const POLYLLM_SEVEN_DAY_PLAN: ProductType = {
  subscription_type: '',
  trial_currency: '',
  id: '189f12ae-6930-4e05-af86-52f8994b8e09',
  name: '7-day Access Plan',
  is_solid: true,
  has_trial_period: true,
  regularity: REGULARITY.MONTH,
  price: 3999,
  currency: 'USD',
  trial_period: 7,
  trial_amount: 200,
  created_at: '2024-01-08T14:50:13Z',
  updated_at: '2024-01-08T14:50:13Z',
  billing_period: 30,
  // @ts-ignore
  description: 'PolyLLM 7-day Access Subscription',
  is_unlimited: true,
  solid_name_for_bi: 'ChatwithAI_plan_39.99_usd_30days_2_trial_VAT',
  new_price_ui: 3999,
  old_price_ui: 3999,
  discount_percentage_ui: 0,
  regularity_ui: REGULARITY.MONTH,
  description_ui: '',
  details_ui: '',
  is_popular_ui: false,
  // @ts-ignore
  bi_is_upsell: false,
  alternative_non_trial_name: 'Monthly Plan',
  tags: [],
};

// Ukraine-only trial plan: $2 trial → $19.99/month (7-day trial). Replaces the
// 7-day Access plan for UA users; other regions keep the default plan set.
const POLYLLM_UA_TRIAL_PLAN: ProductType = {
  subscription_type: '',
  trial_currency: '',
  id: '90395df0-4e68-481c-9e93-6df706fb33f8',
  name: '7-day Plan',
  is_solid: true,
  has_trial_period: true,
  regularity: REGULARITY.MONTH,
  price: 1999,
  currency: 'USD',
  trial_period: 7,
  trial_amount: 200,
  created_at: '2024-01-08T14:50:13Z',
  updated_at: '2024-01-08T14:50:13Z',
  billing_period: 30,
  // @ts-ignore
  description: 'PolyLLM 7-day Access Subscription (UA)',
  is_unlimited: true,
  solid_name_for_bi: 'ChatwithAI_plan_19.99_usd_30days_2_trial',
  new_price_ui: 1999,
  old_price_ui: 1999,
  discount_percentage_ui: 0,
  regularity_ui: REGULARITY.MONTH,
  description_ui: '',
  details_ui: '',
  is_popular_ui: false,
  // @ts-ignore
  bi_is_upsell: false,
  alternative_non_trial_name: 'Monthly Plan',
  tags: [],
};

const productsConfigByProject = {
  [PROJECT.POLYLLM_CHAT]: {
    // TODO(polyllm-pricing): PolyLLM does not have one-time/detector products;
    // these IDs are placeholders reused from JustDone so the shared config shape is satisfied.
    ONETIME_DETECTORS_10_DOLLARS_10K_WORDS_ID:
      '7649b99f-ac67-4203-814a-245ba3d61a2d',
    ONETIME_DETECTORS_20_DOLLARS_20K_WORDS_ID:
      'dd3cde1a-1193-43de-a666-73eb3d06597d',
    ONETIME_DETECTORS_40_DOLLARS_50K_WORDS_ID:
      '52f2c29e-db41-4c4a-b1f2-e6ced91487c3',
    ONETIME_DETECTORS_80_DOLLARS_100K_WORDS_ID:
      'db7b5e57-1ea6-46f0-b0bf-efa7613405c9',
    ANNUAL_SUB_OFFER_ID: 'ce6e8796-b540-434a-a45b-b783a872f897',
    ANNUAL_SUB_OFFER_80_USD_365_DAYS_ID: 'c3488e95-7058-42b6-b3a0-ac111b7958cc',
    MONTHLY_SUB_OFFER_ID: '2b689ff3-6ad5-458c-8f4c-49c77ff82d92',
    UNBLOCK_DETECTOR_TOOLS_10_DOLLARS_ID:
      'ec2d5585-a1e2-4956-b528-e70479300f7e',
    // Paywall plan set, in render order: 7-day trial → Annual → 6-Month →
    // Quarterly. The 6-month offer is baseline since CHW-394 (ab-half-year-plan
    // won); the array order is the grid order, so keep the ID in place.
    CHAT_PAYWALL_OFFER_IDS: [
      'c252a6e8-eb2d-488a-9acc-610e00009a1e',
      'db0809e6-cf9a-4044-b65f-15960844c378',
      'eb0d1ea7-8f29-4f91-afe0-78df7a11a165',
      'c7c930d6-1cc4-4992-8793-d80ea5ca98c2',
    ],
    SUB_NOT_FOUND_PRODUCTS: [
      POLYLLM_ANNUAL_PLAN,
      POLYLLM_MONTHLY_PLAN,
      POLYLLM_SEVEN_DAY_PLAN,
    ],
    SUB_NOT_FOUND_PRODUCTS_BY_REGION: {
      // Ukraine: swap the 7-day Access plan for the cheaper UA trial plan.
      UA: [POLYLLM_ANNUAL_PLAN, POLYLLM_MONTHLY_PLAN, POLYLLM_UA_TRIAL_PLAN],
    },
  },
} satisfies Readonly<Partial<Record<PROJECT, ProductsConfig>>>;

export const PRODUCTS_PROJECT_CONFIG =
  productsConfigByProject[projectConfig.project] ||
  productsConfigByProject[PROJECT.POLYLLM_CHAT];

/**
 * Resolve the "subscription not found" plan set for a user's country, falling
 * back to the project default when the region has no dedicated plan set.
 */
export const resolveSubNotFoundProducts = (country?: string): ProductType[] => {
  const config: ProductsConfig = PRODUCTS_PROJECT_CONFIG;
  const iso = country?.trim().toUpperCase();
  const regional = iso
    ? config.SUB_NOT_FOUND_PRODUCTS_BY_REGION?.[iso]
    : undefined;
  return regional ?? config.SUB_NOT_FOUND_PRODUCTS;
};
